import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target, Search, UserMinus, ArrowRightLeft, Filter, Plus, Mail, Link2, Copy, CheckCircle, 
  UserPlus, Edit2, X, Camera, Trash2, ChevronDown, User, Loader2, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

const POSITIONS = [
  'Goalkeeper', 'Right Back', 'Left Back', 'Center Back', 
  'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder',
  'Right Winger', 'Left Winger', 'Striker', 'Forward'
];

const AGE_RANGES = [
  { label: 'All Ages', value: 'all' },
  { label: 'Under 10', value: 'u10', min: 0, max: 9 },
  { label: 'U10-U12', value: 'u12', min: 10, max: 12 },
  { label: 'U13-U14', value: 'u14', min: 13, max: 14 },
  { label: 'U15-U16', value: 'u16', min: 15, max: 16 },
  { label: 'U17-U18', value: 'u18', min: 17, max: 18 },
  { label: '18+', value: 'adult', min: 18, max: 99 }
];

const ClubAthletes = () => {
  const { club } = useOutletContext();
  const { clubId } = useParams();
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterPosition, setFilterPosition] = useState('all');
  const [filterAge, setFilterAge] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modals
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [showAthleteDetail, setShowAthleteDetail] = useState(false);
  
  // Form states
  const [generatedInviteLink, setGeneratedInviteLink] = useState('');
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [targetTeamId, setTargetTeamId] = useState('');
  const [moving, setMoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  
  const [addForm, setAddForm] = useState({
    athlete_name: '',
    parent_name: '',
    parent_email: '',
    team_id: '',
    position: '',
    birth_date: ''  // Full date YYYY-MM-DD for athlete identification
  });
  
  const [editForm, setEditForm] = useState({
    name: '',
    age: '',
    birth_date: '',
    position: '',
    team_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('club_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [athletesRes, teamsRes] = await Promise.all([
        axios.get(`${API}/clubs/athletes`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/clubs/teams`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      
      // Transform athletes data with proper age calculation
      const transformedAthletes = [];
      (athletesRes.data || []).forEach(athlete => {
        (athlete.teams || []).forEach(teamInfo => {
          // Calculate age from birth_date
          let age = athlete.age;
          if (!age && athlete.birth_date) {
            const birthYear = new Date(athlete.birth_date).getFullYear();
            age = new Date().getFullYear() - birthYear;
          }
          
          transformedAthletes.push({
            id: `${athlete.id}_${teamInfo.team_id}`,
            athlete_id: athlete.athlete_id || athlete.id,
            name: athlete.name,
            parent_name: athlete.parent_name,
            parent_email: athlete.parent_email,
            birth_date: athlete.birth_date,
            age: age,
            position: teamInfo.position || athlete.position,
            jersey_number: teamInfo.jersey_number,
            status: teamInfo.status || athlete.status,
            team_id: teamInfo.team_id,
            team_name: teamInfo.team_name,
            photo_url: athlete.photo_url,
            joined_at: teamInfo.joined_at,
            pass_status: athlete.pass_status
          });
        });
      });
      
      setAthletes(transformedAthletes);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    }
    setLoading(false);
    setRefreshing(false);
  };

  const generateInviteLink = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    const inviteCode = btoa(JSON.stringify({
      clubId,
      teamId,
      clubName: club?.name,
      teamName: team?.name,
      type: 'player',
      created: Date.now()
    }));
    return `${APP_URL}/team/player-join/${inviteCode}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const handleAddAthlete = async (e) => {
    e.preventDefault();
    if (!addForm.team_id) {
      toast.error('Please select a team');
      return;
    }

    setSaving(true);
    const inviteLink = generateInviteLink(addForm.team_id);

    try {
      await axios.post(`${API}/clubs/teams/${addForm.team_id}/athletes`, {
        athlete_name: addForm.athlete_name,
        parent_name: addForm.parent_name,
        parent_email: addForm.parent_email,
        position: addForm.position || undefined,
        birth_date: addForm.birth_date || undefined  // Full birth date for identification
      }, { headers: getAuthHeaders() });
      
      toast.success('Athlete added successfully!');
      setShowAddModal(false);
      setGeneratedInviteLink(inviteLink);
      setShowInviteLinkModal(true);
      setAddForm({ athlete_name: '', parent_name: '', parent_email: '', team_id: '', position: '', birth_date: '' });
      fetchData();
    } catch (error) {
      setGeneratedInviteLink(inviteLink);
      setShowAddModal(false);
      setShowInviteLinkModal(true);
      toast.info('Athlete registered. Share the invite link with the parent.');
    }
    setSaving(false);
  };

  const handleGenerateLinkOnly = () => {
    if (!addForm.team_id) {
      toast.error('Please select a team');
      return;
    }
    const inviteLink = generateInviteLink(addForm.team_id);
    setGeneratedInviteLink(inviteLink);
    setShowAddModal(false);
    setShowInviteLinkModal(true);
  };

  const handleMoveAthlete = async () => {
    if (!selectedAthlete || !targetTeamId) return;
    
    setMoving(true);
    try {
      await axios.post(`${API}/clubs/athletes/${selectedAthlete.athlete_id}/move-team`, {
        from_team_id: selectedAthlete.team_id,
        to_team_id: targetTeamId
      }, { headers: getAuthHeaders() });
      
      toast.success('Athlete moved successfully!');
      setShowMoveModal(false);
      setSelectedAthlete(null);
      setTargetTeamId('');
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move athlete');
    }
    setMoving(false);
  };

  const handleEditAthlete = async (e) => {
    e.preventDefault();
    if (!selectedAthlete) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/clubs/athletes/${selectedAthlete.athlete_id}`, {
        name: editForm.name,
        age: editForm.age ? parseInt(editForm.age) : undefined,
        position: editForm.position,
        team_id: selectedAthlete.team_id
      }, { headers: getAuthHeaders() });
      
      toast.success('Athlete updated successfully!');
      setShowEditModal(false);
      setSelectedAthlete(null);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update athlete');
    }
    setSaving(false);
  };

  const handleDeactivate = async (athlete) => {
    if (!confirm(`Remove ${athlete.name} from ${athlete.team_name}?`)) return;
    
    try {
      await axios.delete(`${API}/clubs/teams/${athlete.team_id}/athletes/${athlete.athlete_id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Athlete removed from team');
      fetchData(true);
    } catch (error) {
      toast.error('Failed to remove athlete');
    }
  };

  const openMoveModal = (athlete) => {
    setSelectedAthlete(athlete);
    setTargetTeamId('');
    setShowMoveModal(true);
  };

  const openEditModal = (athlete) => {
    setSelectedAthlete(athlete);
    setEditForm({
      name: athlete.name || '',
      age: athlete.age?.toString() || '',
      position: athlete.position || ''
    });
    setShowEditModal(true);
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || 'Unassigned';
  };

  const getAgeCategory = (age) => {
    if (!age) return null;
    if (age < 10) return 'U10';
    if (age <= 12) return 'U12';
    if (age <= 14) return 'U14';
    if (age <= 16) return 'U16';
    if (age <= 18) return 'U18';
    return 'Senior';
  };

  // Filtering logic
  const filteredAthletes = athletes.filter(athlete => {
    const matchesSearch = 
      athlete.name?.toLowerCase().includes(search.toLowerCase()) ||
      athlete.parent_name?.toLowerCase().includes(search.toLowerCase());
    
    const matchesTeam = filterTeam === 'all' || athlete.team_id === filterTeam;
    
    const matchesPosition = filterPosition === 'all' || athlete.position === filterPosition;
    
    let matchesAge = true;
    if (filterAge !== 'all') {
      const ageRange = AGE_RANGES.find(r => r.value === filterAge);
      if (ageRange && ageRange.min !== undefined) {
        matchesAge = athlete.age >= ageRange.min && athlete.age <= ageRange.max;
      }
    }
    
    return matchesSearch && matchesTeam && matchesPosition && matchesAge;
  });

  const clearFilters = () => {
    setSearch('');
    setFilterTeam('all');
    setFilterPosition('all');
    setFilterAge('all');
  };

  const hasActiveFilters = search || filterTeam !== 'all' || filterPosition !== 'all' || filterAge !== 'all';

  // Stats
  const stats = {
    total: athletes.length,
    active: athletes.filter(a => a.status === 'active').length,
    withPass: athletes.filter(a => a.pass_status === 'active').length,
    positions: [...new Set(athletes.map(a => a.position).filter(Boolean))].length
  };

  return (
    <div className="space-y-6" data-testid="club-athletes">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase">
            Club <span className="text-primary">Athletes</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage all athletes across your club's teams
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="border-border"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </Button>
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-primary text-black hover:bg-primary/90"
            disabled={teams.length === 0}
          >
            <UserPlus size={18} className="mr-2" />
            Add Athlete
          </Button>
        </div>
      </div>

      {teams.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
          <p className="text-sm text-yellow-400">
            Create a team first before adding athletes.
          </p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border/40 rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-sm">
              <User className="text-primary" size={20} />
            </div>
            <div>
              <p className="font-heading text-2xl">{stats.total}</p>
              <p className="text-xs text-muted-foreground uppercase">Total Athletes</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border/40 rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-sm">
              <CheckCircle className="text-emerald-400" size={20} />
            </div>
            <div>
              <p className="font-heading text-2xl text-emerald-400">{stats.active}</p>
              <p className="text-xs text-muted-foreground uppercase">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border/40 rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-sm">
              <Target className="text-blue-400" size={20} />
            </div>
            <div>
              <p className="font-heading text-2xl text-blue-400">{stats.withPass}</p>
              <p className="text-xs text-muted-foreground uppercase">With Pass</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border/40 rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-sm">
              <Target className="text-yellow-400" size={20} />
            </div>
            <div>
              <p className="font-heading text-2xl text-yellow-400">{teams.length}</p>
              <p className="text-xs text-muted-foreground uppercase">Teams</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-card border border-border/40 rounded-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or parent..."
              className="bg-transparent border-border pl-10"
            />
          </div>
          
          {/* Filter Toggle */}
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={`border-border ${hasActiveFilters ? 'border-primary text-primary' : ''}`}
          >
            <Filter size={16} className="mr-2" />
            Filters
            {hasActiveFilters && <span className="ml-2 text-xs bg-primary text-black px-1.5 rounded">Active</span>}
            <ChevronDown size={14} className={`ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
        </div>
        
        {/* Expanded Filters */}
        {showFilters && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 pt-4 border-t border-border/40"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Team Filter */}
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Team</label>
                <select
                  value={filterTeam}
                  onChange={(e) => setFilterTeam(e.target.value)}
                  className="w-full bg-transparent border border-border rounded-sm p-2 text-sm"
                >
                  <option value="all" className="bg-card">All Teams</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id} className="bg-card">{team.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Position Filter */}
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Position</label>
                <select
                  value={filterPosition}
                  onChange={(e) => setFilterPosition(e.target.value)}
                  className="w-full bg-transparent border border-border rounded-sm p-2 text-sm"
                >
                  <option value="all" className="bg-card">All Positions</option>
                  {POSITIONS.map(pos => (
                    <option key={pos} value={pos} className="bg-card">{pos}</option>
                  ))}
                </select>
              </div>
              
              {/* Age Filter */}
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Age Group</label>
                <select
                  value={filterAge}
                  onChange={(e) => setFilterAge(e.target.value)}
                  className="w-full bg-transparent border border-border rounded-sm p-2 text-sm"
                >
                  {AGE_RANGES.map(range => (
                    <option key={range.value} value={range.value} className="bg-card">{range.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {hasActiveFilters && (
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                  <X size={14} className="mr-1" /> Clear Filters
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Athletes Table */}
      {loading ? (
        <div className="text-center py-12 bg-card border border-border/40 rounded-sm">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-sm text-muted-foreground mt-4">Loading athletes...</p>
        </div>
      ) : filteredAthletes.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border/40 rounded-sm">
          <Target className="mx-auto text-muted-foreground mb-4" size={48} />
          <p className="text-muted-foreground mb-2">
            {hasActiveFilters ? 'No athletes found matching filters' : 'No athletes in the club yet'}
          </p>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>Clear Filters</Button>
          )}
        </div>
      ) : (
        <div className="bg-card border border-border/40 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-card/50 border-b border-border/40">
                <tr>
                  <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Athlete</th>
                  <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Team</th>
                  <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Age</th>
                  <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Position</th>
                  <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Status</th>
                  <th className="text-right text-xs uppercase tracking-widest text-muted-foreground p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAthletes.map((athlete, idx) => (
                  <motion.tr
                    key={athlete.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.02 }}
                    className="border-b border-border/20 hover:bg-card/50"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {athlete.photo_url ? (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${athlete.photo_url}`}
                            alt={athlete.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">
                              {athlete.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{athlete.name}</p>
                          {athlete.parent_name && (
                            <p className="text-xs text-muted-foreground">Parent: {athlete.parent_name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm px-2 py-1 bg-blue-500/10 text-blue-400 rounded-sm">
                        {athlete.team_name}
                      </span>
                    </td>
                    <td className="p-4">
                      <div>
                        <span className="text-sm">{athlete.age || '-'}</span>
                        {athlete.age && (
                          <span className="text-xs text-muted-foreground ml-1">
                            ({getAgeCategory(athlete.age)})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm">{athlete.position || '-'}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1">
                        <span className={`text-xs px-2 py-0.5 rounded-sm inline-block w-fit ${
                          athlete.status === 'active' 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {athlete.status}
                        </span>
                        {athlete.pass_status === 'active' && (
                          <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-sm inline-block w-fit">
                            Pass Active
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(athlete)}
                          className="text-muted-foreground hover:text-primary"
                          title="Edit athlete"
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openMoveModal(athlete)}
                          className="text-muted-foreground hover:text-blue-400"
                          title="Move to another team"
                        >
                          <ArrowRightLeft size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeactivate(athlete)}
                          className="text-muted-foreground hover:text-red-400"
                          title="Remove from team"
                        >
                          <UserMinus size={16} />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Table Footer */}
          <div className="p-4 border-t border-border/40 bg-card/30">
            <p className="text-sm text-muted-foreground">
              Showing {filteredAthletes.length} of {athletes.length} athletes
            </p>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
        <p className="text-sm text-primary font-semibold mb-1">Athlete Data Ownership</p>
        <p className="text-xs text-muted-foreground">
          Athletes belong to the club context. Their development history is owned by them, 
          not by individual coaches. Club admin can move athletes between teams while preserving their progress data.
        </p>
      </div>

      {/* Move Athlete Modal */}
      {showMoveModal && selectedAthlete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowMoveModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-heading text-xl uppercase mb-6">Move Athlete</h2>
            
            <div className="mb-6 p-4 bg-muted/30 rounded-sm">
              <div className="flex items-center gap-3">
                {selectedAthlete.photo_url ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${selectedAthlete.photo_url}`}
                    alt={selectedAthlete.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-primary">
                      {selectedAthlete.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-semibold">{selectedAthlete.name}</p>
                  <p className="text-sm text-muted-foreground">
                    From: <span className="text-blue-400">{selectedAthlete.team_name}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                Move to Team
              </label>
              <select
                value={targetTeamId}
                onChange={(e) => setTargetTeamId(e.target.value)}
                className="w-full bg-transparent border border-border rounded-sm p-3"
              >
                <option value="" className="bg-card">Select destination team</option>
                {teams.filter(t => t.id !== selectedAthlete.team_id && t.status === 'active').map(team => (
                  <option key={team.id} value={team.id} className="bg-card">{team.name}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowMoveModal(false)}
                className="flex-1"
                disabled={moving}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleMoveAthlete}
                disabled={!targetTeamId || moving}
                className="flex-1 bg-primary text-black hover:bg-primary/90"
              >
                {moving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {moving ? 'Moving...' : 'Move Athlete'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Athlete Modal */}
      {showEditModal && selectedAthlete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-heading text-xl uppercase mb-6">Edit Athlete</h2>
            
            <form onSubmit={handleEditAthlete} className="space-y-4">
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Name
                </label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  required
                  className="bg-transparent border-border"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Age
                  </label>
                  <Input
                    type="number"
                    value={editForm.age}
                    onChange={(e) => setEditForm({...editForm, age: e.target.value})}
                    className="bg-transparent border-border"
                    min={5}
                    max={50}
                  />
                </div>
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Position
                  </label>
                  <select
                    value={editForm.position}
                    onChange={(e) => setEditForm({...editForm, position: e.target.value})}
                    className="w-full bg-transparent border border-border rounded-sm p-2"
                  >
                    <option value="" className="bg-card">Select position</option>
                    {POSITIONS.map(pos => (
                      <option key={pos} value={pos} className="bg-card">{pos}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowEditModal(false)}
                  className="flex-1"
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-primary text-black hover:bg-primary/90"
                >
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Add Athlete Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowAddModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6 my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-heading text-xl uppercase mb-6">Add New Athlete</h2>
            
            <form onSubmit={handleAddAthlete} className="space-y-4">
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Athlete Name *
                </label>
                <Input
                  value={addForm.athlete_name}
                  onChange={(e) => setAddForm({...addForm, athlete_name: e.target.value})}
                  required
                  className="bg-transparent border-border"
                  placeholder="Athlete full name"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Parent/Guardian Name
                </label>
                <Input
                  value={addForm.parent_name}
                  onChange={(e) => setAddForm({...addForm, parent_name: e.target.value})}
                  className="bg-transparent border-border"
                  placeholder="Parent name"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Parent Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    type="email"
                    value={addForm.parent_email}
                    onChange={(e) => setAddForm({...addForm, parent_email: e.target.value})}
                    className="bg-transparent border-border pl-10"
                    placeholder="parent@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Assign to Team *
                </label>
                <select
                  value={addForm.team_id}
                  onChange={(e) => setAddForm({...addForm, team_id: e.target.value})}
                  required
                  className="w-full bg-transparent border border-border rounded-sm p-3"
                >
                  <option value="" className="bg-card">Select a team</option>
                  {teams.filter(t => t.status === 'active').map(team => (
                    <option key={team.id} value={team.id} className="bg-card">{team.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Position
                  </label>
                  <select
                    value={addForm.position}
                    onChange={(e) => setAddForm({...addForm, position: e.target.value})}
                    className="w-full bg-transparent border border-border rounded-sm p-2"
                  >
                    <option value="" className="bg-card">Select position</option>
                    {POSITIONS.map(pos => (
                      <option key={pos} value={pos} className="bg-card">{pos}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Birth Date <span className="text-xs text-primary">(for athlete identification)</span>
                  </label>
                  <Input
                    type="date"
                    value={addForm.birth_date}
                    onChange={(e) => setAddForm({...addForm, birth_date: e.target.value})}
                    className="bg-transparent border-border"
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Helps link with existing athletes to avoid duplicates
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <Button 
                  type="submit"
                  className="w-full bg-primary text-black hover:bg-primary/90"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Mail size={16} className="mr-2" />}
                  Add & Send Invite Email
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleGenerateLinkOnly}
                  className="w-full border-primary/50 text-primary hover:bg-primary/10"
                >
                  <Link2 size={16} className="mr-2" />
                  Generate Invite Link Only
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setShowAddModal(false)}
                  className="w-full text-muted-foreground"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Invite Link Modal */}
      {showInviteLinkModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowInviteLinkModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-emerald-400" size={32} />
              </div>
              <h2 className="font-heading text-xl uppercase">Invite Link Generated</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Share this link with the parent to join the team
              </p>
            </div>
            
            <div className="bg-card/50 border border-border/40 rounded-sm p-4 mb-4">
              <p className="text-xs text-muted-foreground uppercase mb-2">Athlete Invite Link</p>
              <div className="flex gap-2">
                <Input
                  value={generatedInviteLink}
                  readOnly
                  className="bg-transparent border-border text-xs flex-1"
                />
                <Button
                  onClick={() => copyToClipboard(generatedInviteLink)}
                  className="bg-primary text-black hover:bg-primary/90"
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/30 rounded-sm p-3 mb-4">
              <p className="text-xs text-primary">
                The parent will be able to create their account and access the athlete's team after clicking this link.
              </p>
            </div>

            <Button 
              onClick={() => setShowInviteLinkModal(false)}
              className="w-full bg-emerald-500 text-black hover:bg-emerald-400"
            >
              Done
            </Button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ClubAthletes;
